/**
 * Main Feedback App Component (Enhanced)
 * 
 * Features:
 * - Pill filters (New, Top, Trending)
 * - Advanced filter dropdown (Status, Board, Date)
 * - Detail modal on card click
 * - All feedbacks shown by default
 * - Downvoting support
 * - Auth modal for guests
 */
import { useState, useEffect, useCallback } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import FeedbackList from './components/FeedbackList';
import FeedbackForm from './components/FeedbackForm';
import FeedbackDetailModal from './components/FeedbackDetailModal';
import FilterDropdown from './components/FilterDropdown';
import SortPills from './components/SortPills';
import FilterIndicators from './components/FilterIndicators';
import Pagination from './components/Pagination';
import AuthModal from '../components/AuthModal';

const FeedbackApp = ({ 
    productId, 
    categories,
    statuses,
    restUrl, 
    nonce, 
    isLoggedIn, 
    currentUser,
    allowComments,
    enableDownvoting,
    allowGuestVoting,
    enableGoogleLogin,
    enableEmailSignup,
    loginUrl,
    registerUrl,
    ajaxUrl,
    isPremium,
    perPage = 10
}) => {
    const [feedback, setFeedback] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sortBy, setSortByState] = useState('new');
    const [searchQuery, setSearchQueryState] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [selectedFeedback, setSelectedFeedback] = useState(null);
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [filters, setFilters] = useState({
        status: null,
        board: null,
        date: null
    });
    const [isLimitReached, setIsLimitReached] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    // Wrappers that reset page to 1 on sort/search change.
    const setSortBy = (val) => { setCurrentPage(1); setSortByState(val); };
    const setSearchQuery = (val) => { setCurrentPage(1); setSearchQueryState(val); };

    // Helper to get nonce (fallback to wpApiSettings)
    const getNonce = () => {
        return nonce || (typeof wpApiSettings !== 'undefined' && wpApiSettings.nonce) || '';
    };

    // Fetch feedback
    const fetchFeedback = useCallback(async () => {
        setLoading(true);
        
        const params = new URLSearchParams();
        if (productId) params.append('product', productId);
        if (searchQuery) params.append('search', searchQuery);

        // Pagination params.
        params.append('per_page', perPage);
        params.append('page', currentPage);
        
        // Filter params
        if (filters.status) params.append('status', filters.status);
        if (filters.board) params.append('category', filters.board);
        if (filters.date) params.append('date_filter', filters.date);
        
        // Sort mapping
        if (sortBy === 'new') {
            params.append('orderby', 'date');
            params.append('order', 'DESC');
        } else if (sortBy === 'top') {
            params.append('orderby', 'meta_value_num');
            params.append('meta_key', '_cha_feedback_upvotes');
            params.append('order', 'DESC');
        } else if (sortBy === 'trending') {
            params.append('orderby', 'meta_value_num');
            params.append('meta_key', '_cha_feedback_upvotes');
            params.append('order', 'DESC');
            params.append('date_filter', 'this_week');
        }

        try {
            const response = await fetch(`${restUrl}?${params}`, {
                credentials: 'same-origin',
                headers: { 'X-WP-Nonce': getNonce() }
            });
            const data = await response.json();           
            setFeedback(data.items || []);
            setTotalPages(data.total_pages || 1);
            setIsLimitReached(!!data.is_limit_reached);
        } catch (error) {
            console.error('Failed to fetch feedback:', error);
            setFeedback([]);
        } finally {
            setLoading(false);
        }
    }, [productId, searchQuery, sortBy, filters, currentPage, perPage, restUrl, nonce]);

    useEffect(() => {
        fetchFeedback();
    }, [fetchFeedback]);

    // Handle upvote
    const handleUpvote = async (feedbackId) => {
        // Check guest voting permissions
        if (!isLoggedIn && !allowGuestVoting) {
            setShowAuthModal(true);
            return;
        }

        try {
            const response = await fetch(`${restUrl}/${feedbackId}/upvote`, {
                method: 'POST',
                credentials: 'same-origin',
                headers: { 'X-WP-Nonce': getNonce() }
            });
            const result = await response.json();
            // Update local state with full vote info
            setFeedback(prev => prev.map(item => {
                if (item.id === feedbackId) {
                    return {
                        ...item,
                        upvotes: result.upvotes ?? result.count,
                        downvotes: result.downvotes ?? 0,
                        net_votes: result.net_votes ?? result.count,
                        has_upvoted: result.action === 'added',
                        has_downvoted: false,
                        has_voted: result.action === 'added'
                    };
                }
                return item;
            }));

            // Update selected feedback if open in modal
            if (selectedFeedback && selectedFeedback.id === feedbackId) {
                setSelectedFeedback(prev => ({
                    ...prev,
                    upvotes: result.upvotes ?? result.count,
                    downvotes: result.downvotes ?? 0,
                    net_votes: result.net_votes ?? result.count,
                    has_upvoted: result.action === 'added',
                    has_downvoted: false,
                    has_voted: result.action === 'added'
                }));
            }
        } catch (error) {
            console.error('Failed to upvote:', error);
        }
    };

    // Handle downvote
    const handleDownvote = async (feedbackId) => {
        if (!enableDownvoting) return;
        
        // Check guest voting permissions
        if (!isLoggedIn && !allowGuestVoting) {
            setShowAuthModal(true);
            return;
        }        

        try {
            const response = await fetch(`${restUrl}/${feedbackId}/downvote`, {
                method: 'POST',
                credentials: 'same-origin',
                headers: { 'X-WP-Nonce': getNonce() }
            });
            const result = await response.json();

            // Update local state
            setFeedback(prev => prev.map(item => {
                if (item.id === feedbackId) {
                    return {
                        ...item,
                        upvotes: result.upvotes,
                        downvotes: result.downvotes,
                        net_votes: result.net_votes,
                        has_upvoted: false,
                        has_downvoted: result.action === 'added',
                        has_voted: false
                    };
                }
                return item;
            }));

            // Update selected feedback if open in modal
            if (selectedFeedback && selectedFeedback.id === feedbackId) {
                setSelectedFeedback(prev => ({
                    ...prev,
                    upvotes: result.upvotes,
                    downvotes: result.downvotes,
                    net_votes: result.net_votes,
                    has_upvoted: false,
                    has_downvoted: result.action === 'added',
                    has_voted: false
                }));
            }
        } catch (error) {
            console.error('Failed to downvote:', error);
        }
    };

    // Handle card click to open detail modal
    const handleCardClick = async (feedbackItem) => {
        // Optimization: Use existing data if we have the content
        if (feedbackItem && feedbackItem.content !== undefined) {
            setSelectedFeedback(feedbackItem);
            return;
        }

        try {
            const response = await fetch(`${restUrl}/${feedbackItem.id}`, {
                credentials: 'same-origin',
                headers: { 'X-WP-Nonce': getNonce() }
            });
            const fullData = await response.json();
            setSelectedFeedback(fullData);
        } catch (error) {
            console.error('Failed to fetch feedback details:', error);
            // Fallback to existing data
            setSelectedFeedback(feedbackItem);
        }
    };

    // Handle submit
    const handleSubmit = async (formData) => {
        try {
            const response = await fetch(restUrl, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': getNonce()
                },
                body: JSON.stringify({
                    ...formData,
                    product_id: productId
                })
            });

            if (response.ok) {
                setShowForm(false);
                fetchFeedback();
            } else {
                const error = await response.json();
                alert(error.message || __( 'Failed to submit feedback', 'changeloger' ));
            }
        } catch (error) {
            console.error('Failed to submit:', error);
            alert(__( 'Failed to submit feedback', 'changeloger' ));
        }
    };

    // Handle filter change — reset to page 1.
    const handleFilterChange = (filterType, value) => {
        setCurrentPage(1);
        if (filterType === 'clear') {
            setFilters({ status: null, board: null, date: null });
        } else {
            setFilters(prev => ({
                ...prev,
                [filterType]: value === prev[filterType] ? null : value
            }));
        }
    };

    // Handle page change — scroll feedback container into view.
    const handlePageChange = (page) => {
        setCurrentPage(page);
        const el = document.querySelector('.cha-feedback-frontend');
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    // Handle submit button click - always require login for feedback submission
    const handleSubmitClick = () => {
        // User must be logged in to submit feedback
        if (!isLoggedIn) {
            setShowAuthModal(true);
            return;
        }
        setShowForm(true);
    };

    return (
        <div className="cha-feedback-frontend">
            {/* Header */}
            <div className="cha-feedback-header">
                <h2 className="cha-feedback-title">{__( 'Feature Requests & Feedback', 'changeloger' )}</h2>
                
             

                {/* Hide submit button if limit reached for free users */}
                {(!isLimitReached || isPremium) && (
                    <button 
                        className="cha-btn cha-btn-primary"
                        onClick={handleSubmitClick}
                    >
                        + {__( 'Submit Feedback', 'changeloger' )}
                    </button>
                )}
            </div>
                
            {/* Admin Warning Notice */}
                {isLimitReached && !isPremium && currentUser?.roles?.includes('administrator') && (
                    <div className="cha-alert cha-alert-warning" style={{ margin: '0 20px', flex: 1 }}>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="20" height="20" style={{ marginRight: '8px', verticalAlign: 'middle' }}>
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <span>{__( 'Feature request limit is reached for free users. Please upgrade to Pro for unlimited feedback.', 'changeloger' )}</span>
                    </div>
                )}

            {/* Sort Pills and Filters Row */}
            <div className="cha-feedback-toolbar">
                <SortPills 
                    activeSort={sortBy}
                    onSortChange={setSortBy}
                />
                
                <div className="cha-toolbar-right">
                    <div className="cha-search-box">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="18" height="18">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <input
                            type="text"
                            placeholder={__( 'Search', 'changeloger' )}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        {searchQuery && (
                            <button 
                                className="cha-search-clear"
                                onClick={() => setSearchQuery('')}
                            >
                                ×
                            </button>
                        )}
                    </div>
                    
                    <FilterDropdown
                        statuses={statuses}
                        categories={categories}
                        activeFilters={filters}
                        onFilterChange={handleFilterChange}
                    />
                </div>
            </div>

            {/* Active Filter Indicators */}
            <FilterIndicators
                filters={filters}
                statuses={statuses}
                categories={categories}
                onRemoveFilter={(filterType) => handleFilterChange(filterType, null)}
            />

            {/* Feedback List */}
            <FeedbackList 
                feedback={feedback}
                loading={loading}
                statuses={statuses}
                onUpvote={handleUpvote}
                onDownvote={handleDownvote}
                onCardClick={handleCardClick}
                isLoggedIn={isLoggedIn}
                isPremium={isPremium}
                currentUser={currentUser}
                enableDownvoting={enableDownvoting}
                onAuthRequired={() => setShowAuthModal(true)}
            />

            {/* Pagination */}
            {!loading && totalPages > 1 && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                />
            )}

            {/* Submit Form Modal */}
            {showForm && (
                <FeedbackForm 
                    categories={categories}
                    currentUser={currentUser}
                    onSubmit={handleSubmit}
                    onClose={() => setShowForm(false)}
                />
            )}

            {/* Detail Modal */}
            {selectedFeedback && (
                <FeedbackDetailModal
                    feedback={selectedFeedback}
                    statuses={statuses}
                    onClose={() => setSelectedFeedback(null)}
                    onUpvote={handleUpvote}
                    onDownvote={handleDownvote}
                    isLoggedIn={isLoggedIn}
                    isPremium={isPremium}
                    currentUser={currentUser}
                    restUrl={restUrl}
                    nonce={nonce}
                    allowComments={allowComments}
                    enableDownvoting={enableDownvoting}
                    onAuthRequired={() => setShowAuthModal(true)}
                    onCommentCountChange={(feedbackId, newCount) => {
                        setFeedback(prev => prev.map(item => 
                            item.id === feedbackId 
                                ? { ...item, comment_count: newCount } 
                                : item
                        ));
                    }}
                />
            )}

            {/* Auth Modal */}
            <AuthModal
                isOpen={showAuthModal}
                onClose={() => setShowAuthModal(false)}
                onLoginSuccess={() => window.location.reload()}
                enableGoogleLogin={enableGoogleLogin}
                enableEmailSignup={enableEmailSignup}
                loginUrl={loginUrl}
                registerUrl={registerUrl}
                ajaxUrl={ajaxUrl}
            />
        </div>
    );
};

export default FeedbackApp;
